import { useMemo } from 'react';
import { ColorParams } from '@bynder-studio/render-core';
import { getExistingBrandColor } from 'packages/pages/editor/RightSideMenu/FormComponents/EditorColor/utils';
import { rgbToHex } from 'packages/pages/editor/RightSideMenu/FormComponents/ColorPicker/utils';
import { BrandColor, BrandColorPalette } from 'packages/store/brandColors/types';
import { BrandColorOnElement } from 'packages/pages/editor/RightSideMenu/FormComponents/EditorColor';
import { extractUnique } from 'packages/pages/editor/RightSideMenu/Shots/Text/Fonts/utils';
import { brandColor2ColorParams, colorToCSS } from 'packages/common/editor/editorHelper';

type Props = {
    colors: ColorParams[];
    elementColors: ColorParams[];
    palettes: BrandColorPalette[];
    updateSingle: (newColor: ColorParams) => void;
    updateEachRun: (buildValue: (existingColor: ColorParams) => ColorParams) => void;
};

const useColorManipulatorCore = ({ colors, elementColors, palettes, updateSingle, updateEachRun }: Props) => {
    const setColor = (newValue: ColorParams) => {
        if (colors.length > 1) {
            updateEachRun(() => ({ ...newValue, brandColorId: null }));
        } else {
            updateSingle({ ...newValue, brandColorId: null });
        }
    };

    const onChange = (val: ColorParams) => {
        updateSingle({ ...val, brandColorId: null });
    };

    const [brandColors, customColors]: [BrandColorOnElement[] | [], ColorParams[] | []] = useMemo(() => {
        const brandColorObj = {};
        const customColorObj = {};

        colors.forEach((c) => {
            const brandColor = !c.brandColorId ? null : getExistingBrandColor(palettes, c.brandColorId);

            if (brandColor && c.brandColorId) {
                brandColorObj[c.brandColorId] = brandColor;
            } else {
                const hexCode = rgbToHex(colorToCSS(c));
                customColorObj[hexCode] = c;
            }
        });

        return [Object.values(brandColorObj), Object.values(customColorObj)];
    }, [colors, palettes]);

    const setBrandColor = (color: BrandColor) => {
        updateSingle({ brandColorId: color.id, ...brandColor2ColorParams(color) });
    };

    const onColorDetach = () => {
        if (colors.length === 1) {
            updateSingle({ ...colors[0], brandColorId: null });
        } else {
            updateEachRun((c: ColorParams) => ({ ...c, brandColorId: null }));
        }
    };

    const onAppliedClick = (color: BrandColor | ColorParams) => {
        if ('id' in color) {
            setBrandColor(color);
        } else {
            setColor(color);
        }
    };

    const isMixed = useMemo(
        () =>
            extractUnique(
                colors.map((c) => {
                    // @ts-ignore
                    const { opacity, brandColorId, ...others } = c;

                    return others;
                }),
            ).length > 1,
        [colors],
    );

    // @ts-ignore
    const isAlphaMixed = useMemo(() => [...new Set(colors.map((c) => c.opacity))].length > 1, [colors]);

    return {
        hasBrandColors: !!brandColors.length,
        isMixed,
        isAlphaMixed,
        palettes,
        brandColors,
        customColors,
        onChange,
        onAppliedClick,
        setBrandColor,
        onColorDetach,
        colors,
        elementColors,
    };
};

export default useColorManipulatorCore;
